import { nanoid } from 'nanoid';

import { BOARD_STATUS_LABEL, isBoardStatus, type BoardStatus } from '@/pages/Crew/crewRoster';
import type { MappedPersonForSelection } from '@/store/persons/person.dto';
import { GAL_PER_L, round1, toApiVolumeUnit, type VolumeUnit } from '@/utils/unitConversion';

import type {
  DailyLog,
  DailyLogCrewEntry,
  DailyLogDayWorker,
  DailyLogEntry,
  DailyLogShoreConnections,
  DailyLogSignatures,
  DailyLogStats,
  DailyLogTankLevels,
  DailyLogHeader,
} from '../../../store/vessels/vessel.dto';

import { isEmptyDayWorkerRow } from './dayWorkerValidation';
import type { DailyLogFormState, YNValue } from './types';

function ynToBoolean(val: YNValue): boolean {
  return val === 'Y';
}

/**
 * A crew row's status, out of the record's `duty` field.
 *
 * The field is a free string server-side and the roster used to write four
 * values into it — On Duty, Off Duty, Watch, Leave — so an entry saved back then
 * still holds one of those. Anything naming leave reads as on leave; everything
 * else, including a blank, reads as on board, which is what a name on a day's
 * roster means unless it says otherwise.
 */
function toCrewStatus(duty: string): BoardStatus {
  if (isBoardStatus(duty)) return duty;
  return /leave/i.test(duty) ? 'on_leave' : 'on_board';
}

function booleanToYN(val: boolean): YNValue {
  return val ? 'Y' : 'N';
}

function toMooringFlag(val: unknown): boolean {
  if (typeof val === 'boolean') return val;
  if (typeof val === 'string') return val.trim() !== '' && val.trim().toLowerCase() !== 'n';
  return false;
}

/**
 * The berth states go back as text: the service types them `*string` and rejects
 * a boolean outright ("cannot unmarshal bool into Go struct field
 * CreateDailyLogRequest.header.alongside of type string").
 *
 * `Y`/`N` rather than `true`/`false` or an empty string, for two reasons: the
 * daily log PDF prints the value straight after its label, so the sheet reads
 * "ALONGSIDE: Y" the way the paper log it replaces is ticked; and it is what
 * `toMooringFlag` above already reads back as set — the form has always accepted
 * text here, because the service has always sent it.
 */
function fromMooringFlag(val: boolean): string {
  return val ? 'Y' : 'N';
}

function toHeader(s: DailyLogFormState): DailyLogHeader {
  return {
    alongside: fromMooringFlag(s.alongside),
    anchored: fromMooringFlag(s.anchored),
    country: s.country,
    date: new Date(s.date).toISOString(),
    location: s.location,
    stern_to: fromMooringFlag(s.sternTo),
  };
}

function toCrew(s: DailyLogFormState): DailyLogCrewEntry[] {
  return s.crewRoster.map((e) => ({
    crew_id: e.userId,
    day_off: e.dayOff,
    // The status, in words: the field is free text and the daily log PDF prints
    // it straight after the name, so the sheet reads "On leave" the way the
    // paper log it replaces was written.
    duty: BOARD_STATUS_LABEL[e.status],
    holiday: e.hol,
  }));
}

function toDayWorkers(s: DailyLogFormState): DailyLogDayWorker[] {
  // A row added but never typed into is not a worker, so it is not saved as one.
  return s.dayWorkers
    .filter((e) => !isEmptyDayWorkerRow(e))
    .map((e) => ({
      contact: e.contact,
      // A rate left blank, or still mid-typing at just ".", is no rate at all.
      daily_rate: Number(e.dailyRate) || 0,
      email: e.email,
      name: e.name.trim(),
    }));
}

function toEntries(entries: { text: string }[]): DailyLogEntry[] {
  return entries.map((e) => ({ entry: e.text }));
}

function toShoreConnections(s: DailyLogFormState): DailyLogShoreConnections {
  return {
    internet: ynToBoolean(s.shoreConnections.internet),
    phone: ynToBoolean(s.shoreConnections.phone),
    shore_power: ynToBoolean(s.shoreConnections.power),
    water_supply: ynToBoolean(s.shoreConnections.water),
  };
}

/**
 * The levels go out as keyed, with the unit they were keyed in: the service
 * converts to liters itself, and reads a missing unit as gallons — so liters
 * sent without one would be stored 3.785 times too large.
 */
function toTankLevels(s: DailyLogFormState, volumeUnit: VolumeUnit = 'L'): DailyLogTankLevels {
  return {
    black_water_liters: Number(s.tankLevels.blackWater),
    fuel_liters: Number(s.tankLevels.fuel),
    grey_water_liters: Number(s.tankLevels.greyWater),
    water_liters: Number(s.tankLevels.water),
    volume_units: toApiVolumeUnit(volumeUnit),
  };
}

function toStats(s: DailyLogFormState): DailyLogStats {
  return {
    owner_guest_meals: Number(s.ownerGuestMeals),
    underway_days: Number(s.underwayDays),
    usage_days: Number(s.usageDays),
  };
}

function toSignatures(s: DailyLogFormState): DailyLogSignatures {
  return {
    captain_id: s.captainSignature.personId ?? null,
    captain_signed_at: s.captainSignature.signedAt ?? null,
    first_officer_id: s.officerSignature.personId ?? null,
    first_officer_signed_at: s.officerSignature.signedAt ?? null,
  };
}

function toWatchkeeperIds(ids: unknown): string[] {
  if (!Array.isArray(ids)) return [];
  return ids
    .map((v) => (typeof v === 'string' ? v : (v as { id?: string })?.id))
    .filter((v): v is string => typeof v === 'string' && v.length > 0);
}

export function formStateToDailyLog(s: DailyLogFormState, volumeUnit: VolumeUnit = 'L'): DailyLog {
  return {
    header: toHeader(s),
    crew: toCrew(s),
    day_workers: toDayWorkers(s),
    narrative: toEntries(s.narrativeEntries),
    drills: toEntries(s.drillEntries),
    shore_connections: toShoreConnections(s),
    tank_levels: toTankLevels(s, volumeUnit),
    stats: toStats(s),
    signatures: toSignatures(s),
    watchkeepers: toWatchkeeperIds(s.watchkeeperIds),
  };
}

export function dailyLogToFormState(
  log: DailyLog,
  people: MappedPersonForSelection[],
  volumeUnit: VolumeUnit = 'L',
): DailyLogFormState {
  const peopleMap = new Map(people.map((p) => [p.id, p]));
  const fromLiters = (liters: number) =>
    String(volumeUnit === 'gal' ? round1(liters * GAL_PER_L) : liters);

  return {
    location: log.header.location,
    country: log.header.country,
    date: log.header.date,
    sternTo: toMooringFlag(log.header.stern_to),
    alongside: toMooringFlag(log.header.alongside),
    anchored: toMooringFlag(log.header.anchored),
    watchkeeperIds: toWatchkeeperIds(log.watchkeepers),
    crewRoster: log.crew.map((e) => {
      const person = peopleMap.get(e.crew_id);
      return {
        userId: e.crew_id,
        firstName: person?.firstName ?? '',
        lastName: person?.lastName ?? '',
        avatar: person?.avatar ?? '',
        roleName: '',
        status: toCrewStatus(e.duty),
        dayOff: e.day_off,
        hol: e.holiday,
      };
    }),
    narrativeEntries: log.narrative.map((e) => ({ id: nanoid(), text: e.entry })),
    drillEntries: log.drills.map((e) => ({ id: nanoid(), text: e.entry })),
    shoreConnections: {
      power: booleanToYN(log.shore_connections.shore_power),
      water: booleanToYN(log.shore_connections.water_supply),
      phone: booleanToYN(log.shore_connections.phone),
      internet: booleanToYN(log.shore_connections.internet),
    },
    tankLevels: {
      fuel: fromLiters(log.tank_levels.fuel_liters),
      water: fromLiters(log.tank_levels.water_liters),
      greyWater: fromLiters(log.tank_levels.grey_water_liters),
      blackWater: fromLiters(log.tank_levels.black_water_liters),
    },
    usageDays: String(log.stats.usage_days),
    underwayDays: String(log.stats.underway_days),
    ownerGuestMeals: String(log.stats.owner_guest_meals),
    dayWorkers: log.day_workers.map((e) => ({
      id: nanoid(),
      name: e.name,
      contact: e.contact,
      dailyRate: String(e.daily_rate),
      email: e.email,
    })),
    captainSignature: {
      personId: log.signatures.captain_id,
      signedAt: log.signatures.captain_signed_at ?? null,
    },
    officerSignature: {
      personId: log.signatures.first_officer_id,
      signedAt: log.signatures.first_officer_signed_at ?? null,
    },
  };
}
